/**
 * buildSignOffReport.js
 * Generates a self-contained HTML QA sign-off report from assembled slot GROUPS
 * (the output of groupLifecyclesBySlot). Confidence-graded: deterministic
 * pass/warn/fail vs heuristic "review". A session can only be signed off when
 * there are zero hard failures AND every review item has been acknowledged.
 *
 * Can be downloaded directly or opened in a new tab for print-to-PDF.
 *
 * WP2 (frontend-validation-port-spec.md §4): bid health by app×platform,
 * demand-wasted (bidNoFill), the orphan/duplicate report, and the
 * cust_params rule findings are now all SERVER-COMPUTED verdicts, passed in
 * as plain arguments (`bidHealthByAppPlatform` / `bidNoFillReport` /
 * `orphanReport` / `custParamsRuleResults`) by the caller
 * (InspectraNetworkView.jsx — the lifecycle three from the same GET
 * /proxy/lifecycle?grouped=1&detail=1&full=1 fetch that already produces
 * `groups`, cust_params rule findings from a dedicated
 * POST /proxy/validation/cust-params-rules carrying the active profile's
 * rules as a request parameter) — this file computes none of them itself
 * anymore. This function stays a pure function of plain arguments with NO
 * React import; only formatting stays here (`fmtCpm`, `SRC_LABEL`'s
 * display-label lookup, HTML string-building). Per the spec's stricter rule
 * for SIGNED artifacts (§5 recipe #3): the caller is expected to have
 * already verified these fields are present in the server response before
 * calling this function at all — an older proxy during a deploy-skew window
 * that omits one of them should BLOCK report generation with a clear
 * message, never fall back to computing it locally and producing a second,
 * divergent answer. See InspectraNetworkView.jsx's sign-off button for that
 * presence check and its blocking message.
 */

import { SRC_LABEL } from '../validation/bidNoFill';

function fmtCpm(v) {
  if (v == null) return '—';
  const n = parseFloat(v);
  return isNaN(n) ? String(v) : `$${n.toFixed(2)}`;
}
function fmtMs(ms) {
  if (ms == null || ms < 0) return '—';
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}
function esc(s = '') {
  return String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
function shortSlot(adUnit = '') {
  return adUnit.split('/').filter(Boolean).slice(-2).join('/') || adUnit;
}
function verdict(status) {
  if (status === 'pass')   return { label: 'PASS',   color: '#1a6f3c', bg: '#edfaf3' };
  if (status === 'review') return { label: 'REVIEW', color: '#1c5f8a', bg: '#eef6fc' };
  if (status === 'warn')   return { label: 'WARN',   color: '#854f0b', bg: '#fffbea' };
  return { label: 'FAIL', color: '#8a1a1a', bg: '#fff0f0' };
}
function phaseCell(ok, label) {
  return ok
    ? `<td style="text-align:center;color:#1a6f3c;font-weight:600">✓${label ? ' ' + esc(label) : ''}</td>`
    : `<td style="text-align:center;color:#aaa">—</td>`;
}

// WP6 — confidence tier styling for lifecycle links (proven|inferred|guessed).
// Mirrors AdLifecycleView's edge tiers so the report reads the same language
// as the dashboard. Guarded: any unrecognized/missing tier renders nothing.
function tierStyle(tier) {
  if (tier === 'proven')   return { label: 'PROVEN',   color: '#1a6f3c', bg: '#edfaf3' };
  if (tier === 'inferred') return { label: 'INFERRED', color: '#1c5f8a', bg: '#eef6fc' };
  if (tier === 'guessed')  return { label: 'GUESSED',  color: '#854f0b', bg: '#fffbea' };
  return null;
}
// Small inline confidence chip appended after a phase's existing label —
// additive only, never replaces the ✓/— cell content.
function tierChip(tier) {
  const s = tierStyle(tier);
  if (!s) return '';
  return `<span style="display:inline-block;margin-left:4px;padding:0 5px;border-radius:3px;font-size:9px;font-weight:700;letter-spacing:0.03em;background:${s.bg};color:${s.color};border:1px solid ${s.color}40">${s.label}</span>`;
}

// ── Lifecycle graph provenance ──────────────────────────────────────────────
// The signed artifact must always declare WHICH lifecycle graph it was built
// from — the server's (GET /proxy/lifecycle, the same graph agents/CI use
// via the `lifecycle_summary` MCP tool / launch rollup) or, if the caller
// deliberately chose a browser-computed fallback, that fact must be
// unmissable rather than silently ambiguous. Guarded so old/malformed
// `lifecycleSource` values degrade to the server label rather than throwing
// or rendering nothing.
function lifecycleSourceLabel(lifecycleSource) {
  const mode = lifecycleSource && lifecycleSource.mode;
  if (mode === 'browser-fallback') {
    return {
      text: '⚠ Lifecycle graph: computed in-browser (server unavailable) — may differ from the server-side verdict agents/CI see',
      color: '#854f0b',
    };
  }
  return {
    text: '🗄 Lifecycle graph: server-side (same graph agents/CI use)',
    color: '#1a6f3c',
  };
}

// Prominent banner shown when the server graph itself was truncated
// (summarize.js's `truncated` block — never a silent cap). A report that
// only covers part of a session must say so above the fold, not just log it.
function truncationBanner(lifecycleTruncated) {
  if (!lifecycleTruncated || typeof lifecycleTruncated !== 'object') return '';
  const shown = lifecycleTruncated.shown;
  const total = lifecycleTruncated.groups ?? lifecycleTruncated.slots ?? lifecycleTruncated.total;
  if (shown == null || total == null) return '';
  return `
    <div style="margin:0 0 20px;padding:12px 16px;border-radius:8px;background:#fff8e6;border:2px solid #d8b24a;font-size:13px;color:#7a5800;font-weight:700">
      ⚠ TRUNCATED — this report covers ${shown} of ${total} slot group${total !== 1 ? 's' : ''}. It does NOT cover the whole session; treat any "no issues" verdict below as partial.
    </div>`;
}

// ── WP5 corpus/trend-monitor embed helpers ──────────────────────────────────
// (docs/har-corpus-spec.md §8 "sign-off embed"). All of this is defensive:
// the corpus REST routes (proxy-server/apiServer.js) are being built
// concurrently by another workstream and their exact response shape isn't
// locked down yet, so every field access below tolerates missing data and
// the whole section degrades to "" rather than throwing. `corpus` is an
// entirely new, OPTIONAL param to buildSignOffReport() — omitting it (every
// existing caller) produces byte-for-byte the same report as before this
// change; see corpusSection's guard below.

function fmtCorpusPct(v) {
  return typeof v === 'number' && Number.isFinite(v) ? `${Math.round(v * 100)}%` : '—';
}
function fmtCorpusCpm(v) {
  return typeof v === 'number' && Number.isFinite(v) ? `$${v.toFixed(2)}` : '—';
}
function corpusGetAt(obj, path) {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((cur, k) => (cur && typeof cur === 'object' ? cur[k] : undefined), obj);
}
function corpusTopBuyerFill(metrics) {
  const buyerFill = metrics && metrics.buyerFill;
  if (!buyerFill || typeof buyerFill !== 'object') return null;
  let best = null;
  for (const [name, info] of Object.entries(buyerFill)) {
    if (!info) continue;
    const n = typeof info.n === 'number' ? info.n : (typeof info.bidCount === 'number' ? info.bidCount : 0);
    if (!best || n > best.n) best = { name, fillRate: info.fillRate };
  }
  return best;
}
// Normalizes corpus.cohorts into a flat [{platform, metrics, alerts, advisories}]
// array regardless of whether it arrived keyed-by-platform or as an array —
// mirrors TrendsPage.jsx's normalizeScorecard() tolerance for the same
// not-yet-finalized wire shape, kept local here to avoid a pages/ -> utils/
// import (this module has no dependency on the Trends page).
function corpusNormalizeCohorts(cohorts) {
  if (!cohorts) return [];
  if (Array.isArray(cohorts)) {
    return cohorts.filter(c => c && typeof c === 'object').map(c => ({ ...c, platform: String(c.platform || c.platformId || '?').toLowerCase() }));
  }
  if (typeof cohorts === 'object') {
    return Object.entries(cohorts)
      .filter(([, v]) => v && typeof v === 'object')
      .map(([platform, v]) => ({ ...v, platform: platform.toLowerCase() }));
  }
  return [];
}

// Builds the compact per-platform trend strip + advisory block for the
// sign-off report. `corpus` is optional and shaped defensively:
//   { cohorts: {...same per-app×platform shape as GET /proxy/corpus/scorecard},
//     compare: { [platform]: { metrics, alerts, advisories } } }  // optional live-session compare result, e.g. from POST /proxy/corpus/compare
// Returns '' (renders nothing) when `corpus` is absent or carries nothing
// usable — this is the back-compat guard.
function buildCorpusSection(corpus) {
  if (!corpus || typeof corpus !== 'object') return '';
  const cohorts = corpusNormalizeCohorts(corpus.cohorts);
  const compareByPlatform = (corpus.compare && typeof corpus.compare === 'object') ? corpus.compare : {};
  const platforms = [...new Set([...cohorts.map(c => c.platform), ...Object.keys(compareByPlatform)])];
  if (!platforms.length) return '';

  const TILES = [
    { label: 'CPM p50', metricPath: 'cpm.p50', fmt: fmtCorpusCpm },
    { label: 'Bid rate', metricPath: 'bidRate.rate', fmt: fmtCorpusPct },
    { label: 'EIDs presence', metricPath: 'eidsPresence.total.share', fmt: fmtCorpusPct },
    { label: 'Consent presence', metricPath: 'consentPresence.share', fmt: fmtCorpusPct },
  ];

  const cards = platforms.map(platform => {
    const cohort = cohorts.find(c => c.platform === platform) || null;
    const compare = compareByPlatform[platform] || null;
    const cohortMetrics = (cohort && cohort.metrics) || null;
    const liveMetrics = (compare && compare.metrics) || null;
    const metrics = liveMetrics || cohortMetrics;

    const alerts = [...((cohort && cohort.alerts) || []), ...((compare && compare.alerts) || [])].filter(Boolean);
    const advisories = [...((cohort && cohort.advisories) || []), ...((compare && compare.advisories) || [])].filter(Boolean);
    const hasFail = alerts.some(a => a.severity === 'fail');
    const hasWarn = alerts.some(a => a.severity === 'warn');
    const statusColor = hasFail ? '#8a1a1a' : hasWarn ? '#854f0b' : '#1a6f3c';
    const statusLabel = hasFail || hasWarn ? 'WATCH' : (metrics ? 'PASS' : 'OFF');

    const buyerFillTile = (() => {
      const b = corpusTopBuyerFill(metrics);
      return `<div style="min-width:110px"><div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:0.04em">${b ? esc(b.name) + ' fill' : 'Top buyer fill'}</div><div style="font-size:14px;font-weight:700">${b && typeof b.fillRate === 'number' ? fmtCorpusPct(b.fillRate) : '—'}</div></div>`;
    })();

    const tileEls = TILES.map(t => {
      const v = corpusGetAt(metrics, t.metricPath);
      return `<div style="min-width:110px"><div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:0.04em">${esc(t.label)}</div><div style="font-size:14px;font-weight:700">${t.fmt(v)}</div></div>`;
    }).join('') + buyerFillTile;

    const alertLines = alerts.slice(0, 4).map(a => `<div style="font-size:10px;color:${a.severity === 'fail' ? '#8a1a1a' : '#854f0b'}">${a.severity === 'fail' ? '✗' : '⚠'} ${esc(a.summary || a.metric || '')}</div>`).join('');
    const advisoryLines = advisories.slice(0, 6).map(a => {
      const suppressed = !!(a && a.suppressedBy);
      return `<div style="font-size:10px;color:${suppressed ? '#999' : (a.severity === 'warn' ? '#854f0b' : '#1c5f8a')}">${a.severity === 'warn' ? '⚠' : 'ℹ'} ${esc(a.summary || '')}${suppressed ? ' <span style="font-style:italic">(suppressed: test session)</span>' : ''}</div>`;
    }).join('');

    return `
      <div style="border:1px solid #e0e0e0;border-radius:6px;padding:10px 12px;flex:1;min-width:220px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
          <span style="font-weight:700;font-size:12px;text-transform:capitalize">${esc(platform)}</span>
          <span style="display:inline-block;padding:1px 8px;border-radius:4px;font-weight:700;font-size:10px;background:${statusColor}14;color:${statusColor};border:1px solid ${statusColor}40">${statusLabel}</span>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:10px">${tileEls}</div>
        ${alertLines ? `<div style="margin-top:6px">${alertLines}</div>` : ''}
        ${advisoryLines ? `<div style="margin-top:6px;border-top:1px solid #f0f0f0;padding-top:6px">${advisoryLines}</div>` : ''}
      </div>`;
  }).join('');

  return `
    <div class="page-divider">📈 Platform trends (corpus baseline)</div>
    <p style="font-size:12px;color:#666;margin:0 0 8px">Headline metrics vs this app/platform's rolling corpus baseline (trailing 7 days or last 5 sessions, whichever is larger).</p>
    <div style="display:flex;flex-wrap:wrap;gap:12px">${cards}</div>`;
}

// Compact annotation of the group's refresh/redundancy shape.
function refreshNote(g) {
  const parts = [];
  if (g.scrollCount)       parts.push(`♻ scroll ×${g.scrollCount + 1}`);
  if (g.floodCount)        parts.push(`🌊 flood? ${g.slots.length}×`);
  if (g.legitRefreshCount) parts.push(`🔄 ${g.legitRefreshCount}×`);
  if (g.rapidRefreshCount) parts.push(`⚡ ${g.rapidRefreshCount} rapid`);
  if (g.duplicateCount)    parts.push(`dup ${g.duplicateCount}`);
  return parts.join(' ');
}

// Short context label: pageName if present, else content_url host, + visit.
function contextLabel(g) {
  let base = g.pageName || '';
  if (!base) {
    try { base = new URL(g.contentUrl || '').hostname.replace(/^www\./, ''); } catch { base = g.contentUrl || ''; }
  }
  return [base, g.visitLabel].filter(Boolean).join(' · ');
}

// ── cust_params audit helpers ────────────────────────────────────────────────

// Severity → presentation. Mirrors the green/red/yellow convention used in the UI.
function cpStyle(sev) {
  if (sev === 'fail') return { label: 'FAIL', color: '#8a1a1a', bg: '#fff0f0', row: '#fff7f7' };
  if (sev === 'warn') return { label: 'WARN', color: '#854f0b', bg: '#fffbea', row: '#fffdf4' };
  return { label: 'PASS', color: '#1a6f3c', bg: '#edfaf3', row: '#fff' };
}

// Fallback cust_params parse straight from a GAM/IMA URL (slot.custParams is
// normally already present; this covers rows that only carry a raw url).
function parseCpFromUrl(url = '') {
  try {
    const raw = new URL(url).searchParams.get('cust_params') || '';
    return Object.fromEntries(new URLSearchParams(decodeURIComponent(raw)));
  } catch { return {}; }
}

// Pair one slot group's server-computed cust_params rule findings
// (`ruleGroup` — the SERVER-COMPUTED `{ title, scope, results }` shape
// custParamsRulesCheck used to build locally, now `custParamsRuleResults[i]`
// from POST /proxy/validation/cust-params-rules, i-aligned with `groups` by
// the caller — see InspectraNetworkView.jsx's sign-off button) with the
// value that was actually observed on that group's GAM row. `ruleGroup` is
// null when nothing was asserted for this row — either no rules were
// configured for the active profile, or every rule was page/type-filtered
// out. That is a complete, valid answer ("nothing to assert"), NOT a
// missing field; it must never trigger a local recompute.
function collectCustParams(g, ruleGroup) {
  const slot   = g.slots && g.slots[0];
  const gamRow = slot && slot.phases && slot.phases.gam && slot.phases.gam.row;
  const observed = (slot && slot.custParams) || (gamRow && parseCpFromUrl(gamRow.url)) || {};

  let results = [];
  let ranRules = false;
  if (ruleGroup && Array.isArray(ruleGroup.results)) { results = ruleGroup.results; ranRules = true; }

  const rows = results.map(r => {
    const key = String(r.label || '').replace(/^cust_params\./, '');
    const rawVal = observed[key];
    return {
      key,
      value: rawVal === undefined || rawVal === '' ? '—' : String(rawVal),
      sev: r.severity === 'fail' || r.severity === 'warn' ? r.severity : 'pass',
      detail: r.message || '',
    };
  });

  const fails = rows.filter(r => r.sev === 'fail').length;
  const warns = rows.filter(r => r.sev === 'warn').length;
  const passes = rows.length - fails - warns;
  return { g, rows, observed, ranRules, fails, warns, passes };
}

export function buildSignOffReport(groups, {
  tester = '',
  pageUrl = '',
  deviceLabel = '',
  rows: sessionRows = [],
  corpus = null,
  // Provenance of the `groups` this report was built from — see the
  // "Lifecycle graph provenance" helpers above. Defaults to the server
  // label since every current caller (InspectraNetworkView's sign-off
  // button) fetches server groups; a future caller opting into a
  // browser-computed fallback must pass { mode: 'browser-fallback' }
  // explicitly so the report never reads as ambiguous about its source.
  lifecycleSource = { mode: 'server' },
  // The server's `truncated` block ({ groups, shown }), when the fetch that
  // produced `groups` was itself capped. null/omitted = not truncated.
  lifecycleTruncated = null,
  // WP2 (frontend-validation-port-spec.md §4) — server-computed verdict
  // inputs, all three from the same GET /proxy/lifecycle?grouped=1&detail=1
  // &full=1 response `groups` came from (see api/lifecycle.js's
  // getLifecycleGroups). REQUIRED: the caller must have already checked
  // these are present (an older proxy during a deploy-skew window can omit
  // them) and blocked report generation before calling this function at
  // all — see the file header and InspectraNetworkView.jsx's sign-off
  // button. Defaulting to an empty/zeroed shape here is a rendering
  // convenience for callers that already did that check, NOT a silent
  // fallback: this function never recomputes any of the three locally.
  //
  // response.bidHealthByAppPlatform — proxy-server/lifecycle/bidHealth.js's
  // computeBidHealthByAppPlatform (WP3a), array of per-app×platform rows.
  bidHealthByAppPlatform = [],
  // response.bidNoFill (detail=1 → carries .items[]) —
  // proxy-server/lifecycle/bidNoFill.js's computeBidNoFill, computed over
  // the FULL server-side lifecycle set (not scoped to the `groups` this
  // report ends up rendering, which may differ under a display `limit` —
  // see summarize.js: bidNoFill is computed before any group truncation).
  bidNoFillReport = null,
  // response.orphanReport (full=1 only) —
  // proxy-server/lifecycle/orphanReport.js's buildOrphanReport,
  // {orphans, duplicates, counts, reasons}, computed over the full
  // lifecycle set, uncapped.
  orphanReport: serverOrphanReport = null,
  // The fourth and final WP2 verdict: POST /proxy/validation/cust-params-
  // rules's `results[].group`, one entry per element of `groups` (SAME
  // ORDER as the `groups` array passed to this function — the caller posts
  // one row per group, i-aligned, before this function sorts). An entry is
  // null when nothing was asserted for that group (no rules configured for
  // the active profile, or every rule was page/type-filtered out) — a
  // complete answer, not a missing field. REQUIRED like the three above:
  // the caller must have already verified the POST itself succeeded (an
  // array of the right length came back) before calling this function —
  // this file never falls back to computing cust_params findings locally.
  custParamsRuleResults = [],
} = {}) {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  // 4-tier summary counts (from displayStatus).
  const passes  = groups.filter(g => g.displayStatus === 'pass').length;
  const warns   = groups.filter(g => g.displayStatus === 'warn').length;
  const reviews = groups.filter(g => g.displayStatus === 'review').length;
  const fails   = groups.filter(g => g.displayStatus === 'fail').length;

  // Overall: hard failures dominate; otherwise warns; otherwise pending review.
  // (Recomputed below once cust_params failures are known.)
  let overall = fails > 0 ? 'FAIL'
    : warns > 0 ? 'WARN'
    : reviews > 0 ? 'NEEDS REVIEW'
    : 'PASS';
  let overallColor = fails > 0 ? '#8a1a1a' : warns > 0 ? '#854f0b' : reviews > 0 ? '#1c5f8a' : '#1a6f3c';

  // Sort: fail, warn, review, pass.
  const order = { fail: 0, warn: 1, review: 2, pass: 3 };
  const sorted = [...groups].sort((a, b) => (order[a.displayStatus] ?? 4) - (order[b.displayStatus] ?? 4));

  // cust_params audit — pair each group with its SERVER-computed rule
  // findings. `groups` (pre-sort, the caller's original order) and
  // `custParamsRuleResults` are i-aligned by contract; keyed by object
  // identity here so the pairing survives `sorted`'s reordering.
  const cpResultByGroup = new Map(groups.map((g, i) => [g, custParamsRuleResults[i] ?? null]));
  const cpData    = sorted.map(g => collectCustParams(g, cpResultByGroup.get(g)));
  const cpAny     = cpData.some(c => c.ranRules);
  const cpFails   = cpData.reduce((n, c) => n + c.fails, 0);
  const cpWarns   = cpData.reduce((n, c) => n + c.warns, 0);
  const cpPasses  = cpData.reduce((n, c) => n + c.passes, 0);
  const cpChecked = cpFails + cpWarns + cpPasses;

  // Hard failures that block sign-off = deterministic slot fails + cust_params fails.
  const hardFails = fails + cpFails;

  // Fold cust_params verdicts into the overall banner.
  if (cpFails > 0) { overall = 'FAIL'; overallColor = '#8a1a1a'; }
  else if (overall === 'PASS' && cpWarns > 0) { overall = 'WARN'; overallColor = '#854f0b'; }

  // Per-slot rows.
  const rows = sorted.map(g => {
    const lc = g.slots[0] || {};
    const p = lc.phases || {};
    const v = verdict(g.displayStatus);
    const winner = p.prebid?.winner;
    const bidder = winner?.bidder || winner?.seat || winner?.hb_bidder || '—';
    const cpm    = winner?.price  || winner?.hb_pb || null;
    const impLatency = p.impression?.latencyMs;

    // WP6 — confidence chips appended to each phase's existing label (additive;
    // guarded so sessions without lifecycle tiers render exactly as before).
    const prebidLabel = p.prebid?.fired ? `${bidder} ${fmtCpm(cpm)}${tierChip(p.prebid?.tier)}` : '';
    const amazonLabel = p.amazon?.passedToGAM ? tierChip(p.amazon?.tier) : '';
    const gamLabel = p.gam?.hbBidder ? `(${p.gam.hbBidder})` : '';
    const impLabel = p.impression?.fired ? `${fmtMs(impLatency)}${tierChip(p.impression?.tier)}` : '';
    const clickLabel = p.click?.fired ? tierChip(p.click?.tier) : '';

    // WP6 — win-fired indicator: only meaningful when a Prebid winner exists.
    // winFired is undefined on older sessions (no nurl capture) — stay silent then.
    let winFiredNote = '';
    if (p.prebid?.fired && winner) {
      if (p.prebid?.winFired === true) {
        winFiredNote = `<span style="color:#1a6f3c">✓ billable win beacon observed${p.prebid?.winMatchedBy ? ` (${esc(p.prebid.winMatchedBy)})` : ''}</span>`;
      } else if (p.prebid?.winFired === false) {
        winFiredNote = `<span style="color:#854f0b">⚠ winner claimed but no win beacon fired</span>`;
      }
    }

    // WP6 — mediation subsection (spec §2b): only when SDK-bidding buyers were
    // observed/invited for this slot. Always labelled "inferred" — no on-device
    // token proves a server-side mediation bid.
    const mediation = p.mediation;
    let mediationBlock = '';
    if (mediation && Array.isArray(mediation.buyers) && mediation.buyers.length > 0) {
      const buyerRows = mediation.buyers.map(b => {
        const invited = b.reachedViaA3p ? '✓ invited' : '—';
        const fired = (b.rowKeys && b.rowKeys.length > 0) ? `✓ fired (${b.rowKeys.length})` : '—';
        const isWinner = mediation.winner && b.id === mediation.winner;
        const won = isWinner ? '✓ won' : '—';
        return `<div style="display:flex;gap:14px;font-size:11px;color:#444;padding:2px 0">
          <span style="min-width:170px;font-weight:600">${esc(b.name || b.id)}</span>
          <span style="min-width:70px">${invited}</span>
          <span style="min-width:90px">${fired}</span>
          <span style="min-width:60px${isWinner ? ';font-weight:700;color:#1a6f3c' : ''}">${won}</span>
          <span style="color:#888">${esc(b.outcome || '')}</span>
        </div>`;
      }).join('');
      const reasonLines = (mediation.reasons || []).map(r => `<div style="font-size:10px;color:#854f0b">⚑ ${esc(r)}</div>`).join('');
      mediationBlock = `
        <div style="margin-top:6px;padding:6px 8px;background:#fafcff;border:1px solid #e0e8f0;border-radius:5px">
          <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;color:#1c5f8a;margin-bottom:3px">Mediation (inferred)</div>
          ${buyerRows}
          ${reasonLines}
        </div>`;
    }

    // Notes = deterministic redundancy reasons + any auction-level fail/warn issues.
    const detNotes = (g.deterministicReasons || []).map(r => esc(r.detail));
    const issueNotes = (lc.issues || [])
      .filter(i => i.level === 'fail' || i.level === 'error' || i.level === 'warn')
      .map(i => esc(i.msg));
    const note = refreshNote(g);
    const allNotes = [note ? `<span style="color:#888">${note}</span>` : '', winFiredNote, ...detNotes, ...issueNotes]
      .filter(Boolean).join('<br>');

    return `
      <tr>
        <td style="font-weight:600;white-space:nowrap">${esc(shortSlot(g.adUnit))}</td>
        <td style="color:#666;font-size:11px">${esc(contextLabel(g)) || '—'}</td>
        <td style="color:#666;font-size:11px">${esc(g.sizes || '—')}</td>
        ${phaseCell(p.prebid?.fired, prebidLabel)}
        ${phaseCell(p.amazon?.passedToGAM, amazonLabel)}
        ${phaseCell(p.gam?.fired, gamLabel)}
        ${phaseCell(p.impression?.fired, impLabel)}
        ${phaseCell(p.click?.fired, clickLabel)}
        <td style="text-align:center">
          <span style="display:inline-block;padding:2px 10px;border-radius:4px;font-weight:700;font-size:11px;background:${v.bg};color:${v.color};border:1px solid ${v.color}40">
            ${v.label}
          </span>
        </td>
        <td style="font-size:11px;color:#666;max-width:280px">${allNotes}${mediationBlock}</td>
      </tr>`;
  }).join('');

  // Acknowledgement checklist — every NON-fail issue the human must consciously
  // accept: deterministic warnings AND heuristic reviews. Hard fails are NOT
  // here; they block sign-off and must be fixed (never waved through).
  const ackGroups = sorted.filter(g => g.displayStatus === 'warn' || g.displayStatus === 'review');
  const tierBadge = (tier) => tier === 'review'
    ? `<span style="display:inline-block;padding:1px 7px;border-radius:4px;font-size:10px;font-weight:700;background:#eef6fc;color:#1c5f8a;border:1px solid #1c5f8a40">🔍 REVIEW</span>`
    : `<span style="display:inline-block;padding:1px 7px;border-radius:4px;font-size:10px;font-weight:700;background:#fffbea;color:#854f0b;border:1px solid #854f0b40">⚠ WARN</span>`;
  const ackItems = ackGroups.map((g, i) => {
    const tier = g.displayStatus; // 'warn' | 'review'
    const color = tier === 'review' ? '#1c5f8a' : '#854f0b';
    const reasons = [
      ...(g.redundancyReasons || []).map(r => esc(r.detail)),
      ...((g.slots[0]?.issues || [])
        .filter(x => x.level === 'warn' || x.level === 'fail' || x.level === 'error')
        .map(x => esc(x.msg))),
    ].filter(Boolean).join('<br>');
    return `
      <li style="display:flex;gap:10px;align-items:flex-start;padding:10px 0;border-bottom:1px solid #f0f0f0">
        <input type="checkbox" class="ack-check" id="ack${i}" style="margin-top:3px;width:16px;height:16px;flex-shrink:0">
        <label for="ack${i}" style="flex:1;cursor:pointer">
          <div style="font-weight:600;font-size:13px">${tierBadge(tier)} ${esc(shortSlot(g.adUnit))}
            <span style="font-weight:400;color:#888;font-size:11px">— ${esc(contextLabel(g)) || 'session'}</span>
          </div>
          <div style="font-size:12px;color:${color};margin-top:2px">${reasons}</div>
        </label>
      </li>`;
  }).join('');

  // cust_params WARN items also require a conscious human decision before sign-off.
  const cpAckGroups = cpData.filter(c => c.warns > 0);
  const cpAckItems = cpAckGroups.map((c, i) => {
    const warnRows = c.rows.filter(r => r.sev === 'warn');
    const reasons = warnRows
      .map(r => `<code>${esc(r.key)}</code> — ${esc(r.detail)}`)
      .join('<br>');
    return `
      <li style="display:flex;gap:10px;align-items:flex-start;padding:10px 0;border-bottom:1px solid #f0f0f0">
        <input type="checkbox" class="ack-check" id="cpack${i}" style="margin-top:3px;width:16px;height:16px;flex-shrink:0">
        <label for="cpack${i}" style="flex:1;cursor:pointer">
          <div style="font-weight:600;font-size:13px">
            <span style="display:inline-block;padding:1px 7px;border-radius:4px;font-size:10px;font-weight:700;background:#fffbea;color:#854f0b;border:1px solid #854f0b40">🔑 cust_params</span>
            ${esc(shortSlot(c.g.adUnit))}
            <span style="font-weight:400;color:#888;font-size:11px">— ${esc(contextLabel(c.g)) || 'session'}</span>
          </div>
          <div style="font-size:12px;color:#854f0b;margin-top:2px">${reasons}</div>
        </label>
      </li>`;
  }).join('');

  const ackTotal = ackGroups.length + cpAckGroups.length;
  const ackSection = ackTotal ? `
    <div class="page-divider">✋ Sign-off acknowledgements — ${ackTotal} item${ackTotal !== 1 ? 's' : ''} need a human decision</div>
    <p style="font-size:12px;color:#666;margin:0 0 10px">Warnings and inferred (review) findings are <b>not hard failures</b>. Acknowledge each — confirming it's acceptable — to enable sign-off.</p>
    <ul style="list-style:none">${ackItems}${cpAckItems}</ul>
  ` : '';

  // cust_params audit section — one labelled block per GAM slot that has
  // configured rules, each row = a key/value with its pass/warn/fail verdict.
  const cpSlotBlocks = cpData
    .filter(c => c.rows.length > 0)
    .map(c => {
      const v = c.fails > 0 ? cpStyle('fail') : c.warns > 0 ? cpStyle('warn') : cpStyle('pass');
      const ruleRows = c.rows
        // failures first, then warnings, then passes — most actionable on top
        .sort((a, b) => ({ fail: 0, warn: 1, pass: 2 }[a.sev] - { fail: 0, warn: 1, pass: 2 }[b.sev]))
        .map(r => {
          const s = cpStyle(r.sev);
          const val = r.value.length > 70 ? r.value.slice(0, 70) + '…' : r.value;
          return `
            <tr style="background:${s.row}">
              <td style="font-family:ui-monospace,Menlo,monospace;font-weight:600;white-space:nowrap">${esc(r.key)}</td>
              <td style="font-family:ui-monospace,Menlo,monospace;color:#444;word-break:break-all">${esc(val)}</td>
              <td style="text-align:center;white-space:nowrap">
                <span style="display:inline-block;padding:1px 8px;border-radius:4px;font-weight:700;font-size:10px;background:${s.bg};color:${s.color};border:1px solid ${s.color}40">${s.label}</span>
              </td>
              <td style="font-size:11px;color:#666">${esc(r.detail)}</td>
            </tr>`;
        }).join('');
      return `
        <div style="margin:14px 0">
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:6px">
            <span style="font-weight:700;font-size:13px">${esc(shortSlot(c.g.adUnit))}</span>
            <span style="font-size:11px;color:#888">${esc(contextLabel(c.g)) || ''}</span>
            <span style="margin-left:auto;font-size:11px;font-weight:700;color:${v.color}">
              ✓ ${c.passes}${c.warns ? ` · ⚠ ${c.warns}` : ''}${c.fails ? ` · ✗ ${c.fails}` : ''}
            </span>
          </div>
          <table>
            <thead><tr>
              <th style="width:22%">Key</th>
              <th style="width:30%">Value</th>
              <th style="width:10%;text-align:center">Status</th>
              <th style="text-align:left">Rule / detail</th>
            </tr></thead>
            <tbody>${ruleRows}</tbody>
          </table>
        </div>`;
    }).join('');

  const cpSection = cpChecked > 0 ? `
    <div class="page-divider">🔑 cust_params audit — ${cpPasses}/${cpChecked} key checks OK${cpFails ? ` · ${cpFails} flagged` : ''}${cpWarns ? ` · ${cpWarns} warning${cpWarns !== 1 ? 's' : ''}` : ''}</div>
    <p style="font-size:12px;color:#666;margin:0 0 6px">
      Each key below was checked against the active profile's page-scoped cust_params rules
      (enum / regex / required). <b style="color:#8a1a1a">FAIL</b> blocks sign-off; <b style="color:#854f0b">WARN</b> needs acknowledgement.
    </p>
    ${cpSlotBlocks}
  ` : (cpAny ? '' : `
    <div class="page-divider">🔑 cust_params audit</div>
    <p style="font-size:12px;color:#888;margin:0 0 6px">No cust_params rules are configured for the active profile, so no key/value checks were run. Configure rules in <b>Config → Edit profile → cust_params rules</b> to flag missing or unexpected keys here.</p>
  `);

  // Bid Health — header-bidding win rate by app · platform (total vs won).
  // WP2: server-computed (proxy-server/lifecycle/bidHealth.js), passed in
  // as `bidHealthByAppPlatform` — see this function's own param comment.
  const bidHealthData = Array.isArray(bidHealthByAppPlatform) ? bidHealthByAppPlatform : [];
  const bidHealthSection = bidHealthData.length ? `
    <div class="page-divider">📊 Bid Health — header-bidding win rate by app · platform</div>
    <p style="font-size:12px;color:#666;margin:0 0 6px">
      Slots won by each demand source out of filled inventory. A flat <b>0%</b> for Prebid &amp; APS while
      AdX sweeps everything can indicate no demand <i>or</i> the proxy's datacenter egress IP suppressing SSP bids.
    </p>
    <table>
      <thead><tr>
        <th style="text-align:left">App · Platform</th>
        <th style="text-align:center">Requests</th>
        <th style="text-align:center">Filled</th>
        <th style="text-align:center">Prebid won</th>
        <th style="text-align:center">APS won</th>
        <th style="text-align:center">AdX won</th>
        <th style="text-align:center">HB win %</th>
        <th style="text-align:left">Flag</th>
      </tr></thead>
      <tbody>
        ${bidHealthData.map(s => {
          const c  = { fail:'#8a1a1a', warn:'#854f0b', pass:'#1a6f3c', info:'#666' }[s.severity] || '#666';
          const bg = { fail:'#fff5f5', warn:'#fffbea', pass:'#f3fbf6', info:'#fafafa' }[s.severity] || '#fafafa';
          return `<tr style="background:${bg}">
            <td style="font-weight:700;color:${c}">${esc(s.key)}</td>
            <td style="text-align:center">${s.gamRequests}</td>
            <td style="text-align:center">${s.filled}${s.noFill ? ` <span style="color:#999">(+${s.noFill} nf)</span>` : ''}</td>
            <td style="text-align:center;font-weight:600;color:${c}">${s.winMix.prebid}/${s.filled}</td>
            <td style="text-align:center;font-weight:600;color:${c}">${s.winMix.aps}/${s.filled}</td>
            <td style="text-align:center">${s.winMix.adx}/${s.filled}</td>
            <td style="text-align:center;font-weight:800;color:${c}">${s.hbWinRatePct}%</td>
            <td style="font-size:11px;color:${c}">${esc(s.flags[0] || s.headline)}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>
  ` : '';

  // WP6 — orphan/duplicate summary (spec §2d, WP5's buildOrphanReport). Session-
  // level pass over the full unfiltered row set; guarded so old callers that
  // don't pass `orphanReport` just render the "no orphans" line.
  //
  // WP2: server-computed (proxy-server/lifecycle/orphanReport.js), passed in
  // as `serverOrphanReport` — see this function's own param comment. No
  // local recomputation: an absent/malformed field degrades to the empty
  // shape here purely so the report still renders something, matching the
  // "never block report generation on orphan detection" guard this replaced —
  // the caller is what enforces the no-silent-fallback policy before this
  // function is even called.
  const orphanReport = (serverOrphanReport && typeof serverOrphanReport === 'object')
    ? serverOrphanReport
    : { orphans: [], duplicates: [], counts: { total: 0, claimed: 0, orphans: 0, duplicates: 0 }, reasons: [] };
  const orphanCount = orphanReport.counts?.orphans || 0;
  const dupCount = orphanReport.counts?.duplicates || 0;
  const orphanSection = (() => {
    if (orphanCount === 0 && dupCount === 0) {
      return `
        <div class="page-divider">🧭 Orphaned / duplicate requests</div>
        <p style="font-size:12px;color:#888;margin:0 0 6px">No orphaned or duplicate requests detected — every captured ad request was claimed by a slot lifecycle.</p>`;
    }
    const listItems = [
      ...orphanReport.orphans.slice(0, 25).map(o => `<li style="font-size:11px;color:#8a1a1a">orphan · <code>${esc(o.type)}</code> — ${esc(o.reason)}</li>`),
      ...orphanReport.duplicates.slice(0, 25).map(d => `<li style="font-size:11px;color:#854f0b">duplicate · <code>${esc(d.type)}</code> — ${esc(d.reason)}</li>`),
    ].join('');
    const more = (orphanReport.orphans.length + orphanReport.duplicates.length) > 50
      ? `<li style="font-size:11px;color:#999">…and ${(orphanReport.orphans.length + orphanReport.duplicates.length) - 50} more</li>` : '';
    return `
      <div class="page-divider">🧭 Orphaned / duplicate requests — ${orphanCount} orphan${orphanCount !== 1 ? 's' : ''} · ${dupCount} duplicate${dupCount !== 1 ? 's' : ''}</div>
      <p style="font-size:12px;color:#666;margin:0 0 6px">Ad requests never claimed by a slot lifecycle. Duplicates share an identity (correlator+iu, beacon URL, or nurl) with a request that was; orphans share nothing.</p>
      <ul style="list-style:none;padding-left:2px">${listItems}${more}</ul>`;
  })();

  // Demand wasted — ad requests where Prebid/APS bid but the ad server returned
  // no fill. Display slots only; the video equivalent lives in buildVideoSignOff.
  // Advisory: it never moves the verdict banner, because a no-fill can be a
  // legitimate outcome (frequency cap, blocked advertiser) — but a reviewer needs
  // the number in front of them to decide whether it is.
  const demandWastedSection = (() => {
    try {
      // WP2: server-computed (proxy-server/lifecycle/bidNoFill.js), passed in
      // as `bidNoFillReport` — see this function's own param comment. Needs
      // `.items[]` (only present when the fetch that produced it passed
      // detail=1 — see api/lifecycle.js's getLifecycleGroups).
      const nf = bidNoFillReport && Array.isArray(bidNoFillReport.items) ? bidNoFillReport : null;
      if (!nf || !nf.withDemand) return '';
      if (!nf.count) {
        return `
          <div class="page-divider">💸 Demand wasted — bid returned → no fill</div>
          <p style="font-size:12px;color:#1a6f3c;margin:0 0 6px">None — all ${nf.withDemand} ad request${nf.withDemand !== 1 ? 's' : ''} carrying a Prebid or APS bid came back filled.</p>`;
      }
      const srcList = Object.entries(nf.bySource)
        .filter(([, n]) => n > 0)
        .map(([s, n]) => `${SRC_LABEL[s]} ${n}`).join(' · ');
      const itemRows = nf.items.slice(0, 40).map(it => `
        <tr>
          <td style="font-weight:600">${esc(shortSlot(it.adUnit))}</td>
          <td>${esc(it.sources.map(s => SRC_LABEL[s]).join(' + '))}${it.topBidder ? ` <span style="color:#888">(${esc(it.topBidder)})</span>` : ''}</td>
          <td style="text-align:right">${it.bidCpm != null ? fmtCpm(it.bidCpm) : '—'}</td>
          <td style="text-align:center;${it.passedToGAM === false ? 'color:#8a1a1a;font-weight:700' : 'color:#666'}">${it.passedToGAM === false ? 'no' : it.passedToGAM === true ? 'yes' : '—'}</td>
          <td style="font-size:11px;color:#666">${esc(it.reason)}</td>
        </tr>`).join('');
      const more = nf.items.length > 40
        ? `<tr><td colspan="5" style="font-size:11px;color:#999">…and ${nf.items.length - 40} more</td></tr>` : '';
      const notPassedNote = nf.notPassedToAdServer > 0
        ? `<p style="font-size:12px;color:#8a1a1a;margin:0 0 6px"><b>${nf.notPassedToAdServer}</b> of these bids never reached the ad server at all — that is a request-build defect, not an auction outcome.</p>`
        : '';
      return `
        <div class="page-divider">💸 Demand wasted — ${nf.count} of ${nf.withDemand} request${nf.withDemand !== 1 ? 's' : ''} bid → no fill (${nf.rate}%)</div>
        <p style="font-size:12px;color:#666;margin:0 0 6px">${esc(srcList)}${nf.cpmAtRisk > 0 ? ` · ${fmtCpm(nf.cpmAtRisk)} of bid CPM went unserved` : ''}. A no-fill on a slot that had a paid bid in front of it usually means a line item, price bucket or targeting mismatch — not empty inventory. Advisory: does not affect the verdict above.</p>
        ${notPassedNote}
        <table>
          <thead><tr><th>Slot</th><th style="text-align:left">Demand</th><th style="text-align:right">Bid CPM</th><th style="text-align:center">Reached GAM</th><th style="text-align:left">No-fill signal</th></tr></thead>
          <tbody>${itemRows}${more}</tbody>
        </table>`;
    } catch { return ''; }
  })();

  // Optional corpus/trend-monitor embed (WP5) — renders only when the
  // caller passes `corpus`; absent (every pre-existing caller) it's ''.
  const corpusSection = (() => {
    try { return buildCorpusSection(corpus); }
    catch { return ''; }
  })();

  // WP1 'recommend' severity tier (docs/signals-recommendations-watch-spec.md §4) —
  // strictly advisory. Grouped by code from the raw session rows' issues[]; deliberately
  // built AFTER (and independent of) the pass/warn/review/fail tier math above (passes/
  // warns/reviews/fails/overall/overallColor all derive from groups[].displayStatus and
  // are computed higher up in this function — this section never reads or feeds them).
  const recommendationsSection = (() => {
    try {
      const byCode = new Map();
      for (const row of sessionRows || []) {
        const issues = row && Array.isArray(row.issues) ? row.issues : [];
        for (const issue of issues) {
          if (!issue) continue;
          const sev = String(issue.severity || issue.level || '').toLowerCase();
          if (sev !== 'recommend') continue;
          const code = issue.code || 'recommend';
          const note = issue.note || issue.msg || issue.message || '';
          const entry = byCode.get(code) || { code, count: 0, sampleNote: String(note).slice(0, 200) };
          entry.count += 1;
          byCode.set(code, entry);
        }
      }
      const recs = [...byCode.values()].sort((a, b) => b.count - a.count);
      if (!recs.length) return '';
      const recRows = recs.map(r => `
        <tr>
          <td><code>${esc(r.code)}</code></td>
          <td style="text-align:center">${r.count}</td>
          <td>${esc(r.sampleNote)}</td>
        </tr>`).join('');
      return `
        <div class="page-divider">💡 Recommendations</div>
        <p style="font-size:12px;color:#0e7490;margin:0 0 6px">Advisory only — does not affect the verdict above.</p>
        <table>
          <thead><tr><th>Code</th><th style="text-align:center">Count</th><th style="text-align:left">Sample</th></tr></thead>
          <tbody>${recRows}</tbody>
        </table>`;
    } catch { return ''; }
  })();

  // Lifecycle graph provenance — must always be visible on the artifact.
  const srcLabel = lifecycleSourceLabel(lifecycleSource);
  const truncBannerHtml = truncationBanner(lifecycleTruncated);

  const hostLabel = (() => {
    try { return new URL(pageUrl || groups[0]?.pageUrl || 'http://unknown').hostname.replace(/^www\./, ''); }
    catch { return pageUrl || 'Session'; }
  })();

  // Sign-off block — gated by JS: no hard fails AND all review items checked.
  const signoffBlock = `
    <div class="page-divider">✍️ Sign-off</div>
    <div id="signoff-gate" style="display:none;padding:12px 16px;border-radius:8px;background:#fff8e6;border:1px solid #d8b24a;font-size:13px;color:#7a5800;font-weight:600"></div>
    <div id="signoff-blocked" style="display:none;padding:12px 16px;border-radius:8px;background:#fff0f0;border:1px solid #c87a7a;font-size:13px;color:#8a1a1a;font-weight:600">
      ✗ Cannot sign off — ${hardFails} hard failure${hardFails !== 1 ? 's' : ''} must be fixed first${cpFails ? ` (${fails} lifecycle, ${cpFails} cust_params)` : ''}.
    </div>
    <div id="signoff-stamp" style="display:none">
      <div style="padding:14px 18px;border-radius:8px;background:#edfaf3;border:1px solid #4caf7d">
        <div style="font-size:15px;font-weight:800;color:#1a6f3c">✓ Approved for sign-off</div>
        <div style="font-size:12px;color:#444;margin-top:6px">
          ${ackTotal ? `All ${ackTotal} acknowledgement item(s) accepted. ` : ''}No outstanding hard failures${cpChecked ? ` · ${cpPasses}/${cpChecked} cust_params key checks passed` : ''}.
        </div>
        <div style="display:flex;gap:40px;margin-top:18px;font-size:12px;color:#333">
          <div>Tester: <b>${esc(tester) || '________________'}</b></div>
          <div>Date: <b>${dateStr}</b></div>
          <div style="flex:1;border-bottom:1px solid #999;align-self:flex-end">Signature:</div>
        </div>
      </div>
    </div>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>QA Sign-off — ${esc(hostLabel)} — ${dateStr}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #111; background: #fff; padding: 40px; max-width: 1100px; margin: 0 auto; }
    h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
    .meta { font-size: 13px; color: #666; margin-bottom: 28px; }
    .meta span { margin-right: 20px; }
    .summary-bar { display: flex; gap: 16px; align-items: center; padding: 14px 20px; border-radius: 8px; margin-bottom: 28px; border: 2px solid ${overallColor}40; background: #fafafa; }
    .summary-bar .verdict { font-size: 20px; font-weight: 800; color: ${overallColor}; }
    .summary-bar .counts { font-size: 13px; color: #444; }
    .summary-bar .counts span { margin-right: 14px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    thead th { background: #f5f5f7; padding: 9px 12px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #666; border-bottom: 2px solid #e0e0e0; }
    thead th:nth-child(n+4) { text-align: center; }
    tbody tr { border-bottom: 1px solid #f0f0f0; }
    tbody tr:hover { background: #fafafa; }
    tbody td { padding: 9px 12px; vertical-align: top; }
    .page-divider { margin: 28px 0 10px; font-size: 12px; font-weight: 700; color: #444; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e0e0e0; padding-bottom: 6px; }
    footer { margin-top: 32px; font-size: 11px; color: #aaa; border-top: 1px solid #f0f0f0; padding-top: 16px; display: flex; justify-content: space-between; }
    @media print { body { padding: 20px; } .no-print { display: none; } input[type=checkbox] { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body data-fails="${hardFails}" data-acks="${ackTotal}">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px">
    <div>
      <h1>📡 AdLens FX — QA Sign-off Report</h1>
      <div class="meta">
        <span>📄 ${esc(hostLabel)}</span>
        <span>📅 ${dateStr} ${timeStr}</span>
        ${tester ? `<span>👤 ${esc(tester)}</span>` : ''}
        ${deviceLabel ? `<span>📱 ${esc(deviceLabel)}</span>` : ''}
        <span style="color:${srcLabel.color};font-weight:600">${esc(srcLabel.text)}</span>
      </div>
    </div>
    <button class="no-print" onclick="window.print()" style="padding:8px 18px;background:#007aff;color:white;border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer">
      🖨 Print / Save PDF
    </button>
  </div>

  ${truncBannerHtml}

  <div class="summary-bar">
    <div class="verdict">${overall}</div>
    <div class="counts">
      <span style="color:#1a6f3c;font-weight:700">✓ ${passes} pass</span>
      ${warns   > 0 ? `<span style="color:#854f0b;font-weight:700">⚠ ${warns} warn</span>` : ''}
      ${reviews > 0 ? `<span style="color:#1c5f8a;font-weight:700">🔍 ${reviews} review</span>` : ''}
      ${fails   > 0 ? `<span style="color:#8a1a1a;font-weight:700">✗ ${fails} fail</span>` : ''}
      <span style="color:#999">${groups.length} slot${groups.length !== 1 ? 's' : ''} tested</span>
      ${cpChecked > 0 ? `<span style="color:#999">·</span><span style="color:#555;font-weight:600">🔑 cust_params ${cpPasses}/${cpChecked}${cpFails ? ` <span style="color:#8a1a1a">✗${cpFails}</span>` : ''}${cpWarns ? ` <span style="color:#854f0b">⚠${cpWarns}</span>` : ''}</span>` : ''}
    </div>
  </div>

  ${bidHealthSection}

  <table>
    <thead>
      <tr>
        <th>Slot</th>
        <th>Context</th>
        <th>Sizes</th>
        <th>Prebid</th>
        <th>APS</th>
        <th>GAM</th>
        <th>Impression</th>
        <th>Click</th>
        <th>Verdict</th>
        <th style="text-align:left">Notes</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
    </tbody>
  </table>

  ${cpSection}
  ${ackSection}
  ${signoffBlock}
  ${orphanSection}
  ${demandWastedSection}
  ${corpusSection}
  ${recommendationsSection}

  <footer>
    <span>Generated by AdLens FX on ${dateStr} at ${timeStr} · deterministic = confirmed · review = inferred, needs human judgement</span>
    <span>adlensfx.com</span>
  </footer>

  <script>
    (function () {
      var body = document.body;
      var fails = parseInt(body.getAttribute('data-fails') || '0', 10);
      function refresh() {
        var boxes = Array.prototype.slice.call(document.querySelectorAll('.ack-check'));
        var done = boxes.filter(function (b) { return b.checked; }).length;
        var gate = document.getElementById('signoff-gate');
        var stamp = document.getElementById('signoff-stamp');
        var blocked = document.getElementById('signoff-blocked');
        gate.style.display = 'none'; stamp.style.display = 'none'; blocked.style.display = 'none';
        if (fails > 0) { blocked.style.display = 'block'; return; }
        if (done < boxes.length) {
          gate.style.display = 'block';
          gate.textContent = '⚠ ' + (boxes.length - done) + ' of ' + boxes.length + ' item(s) must be acknowledged before sign-off.';
        } else {
          stamp.style.display = 'block';
        }
      }
      document.addEventListener('change', function (e) {
        if (e.target && e.target.classList && e.target.classList.contains('ack-check')) refresh();
      });
      refresh();
    })();
  </script>
</body>
</html>`;
}

/** Download the report as an HTML file */
export function downloadSignOffReport(groups, meta = {}) {
  const html = buildSignOffReport(groups, meta);
  const blob = new Blob([html], { type: 'text/html' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  const date = new Date().toISOString().slice(0, 10);
  a.href     = url;
  a.download = `qa-signoff-${date}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Open a blank tab SYNCHRONOUSLY, from inside the click handler. Browsers only
 * honour window.open for a user gesture, and the report is built after a server
 * round-trip — opening it then is popup-blocked. Returns the window (or null when
 * blocked); pass it to openSignOffReport as `targetWindow`.
 */
export function openBlankReportWindow() {
  try {
    const w = window.open('', '_blank');
    if (w && w.document) {
      try { w.document.title = 'Building sign-off report…'; w.document.body.textContent = 'Building sign-off report…'; } catch { /* cross-origin guard */ }
    }
    return w || null;
  } catch { return null; }
}

/** Close a window from openBlankReportWindow() when report generation aborts. */
export function closeBlankReportWindow(w) {
  try { if (w && !w.closed) w.close(); } catch { /* ignore */ }
}

/**
 * Open report in a new tab (user can print to PDF from there).
 * `targetWindow` is the tab pre-opened in the click handler; when it is missing
 * or was blocked the report falls back to window.open and then to an
 * <a download> so the user always gets the file.
 */
export function openSignOffReport(groups, meta = {}, targetWindow = null) {
  const html = buildSignOffReport(groups, meta);
  const blob = new Blob([html], { type: 'text/html' });
  const url  = URL.createObjectURL(blob);
  let opened = false;
  if (targetWindow && !targetWindow.closed) {
    try { targetWindow.location.href = url; opened = true; } catch { opened = false; }
  }
  if (!opened) {
    let w = null;
    try { w = window.open(url, '_blank'); } catch { w = null; }
    opened = !!w;
  }
  if (!opened) {
    const a = document.createElement('a');
    a.href = url;
    a.download = `qa-signoff-${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 30000);
  return opened ? 'opened' : 'downloaded';
}
